import React, { useState } from "react";
import { motion } from "framer-motion";
import {
    faPhone,
    faEnvelope,
    faGlobe,
    faMapMarkerAlt,
    faClock,
    faPaperPlane,
    faCheckCircle,
    faUser,
    faBuilding
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEmail } from "../hooks/UseEmail";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.2 }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.7, ease: "easeOut" }
    }
};

const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, ease: "easeOut" }
    }
};

const pulseAnimation = {
    initial: { scale: 1 },
    animate: {
        scale: [1, 1.05, 1],
        transition: {
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
        }
    }
};

export const Contact = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const { sendEmail, isSending } = useEmail();

    // useState to control form values
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        city: "",
        topic: "",
        message: "",
    });

    // update state on input change
    const handleChange = (e) => {
        const { id, value } = e.target;
        setForm((prev) => ({ ...prev, [id]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        const { name, email, phone, city, topic, message } = form;

        const emailMsg =
            "Name: " + name + "\n" +
            "Email: " + email + "\n" +
            "Phone: " + phone + "\n" +
            "City: " + city + "\n" +
            "Topic: " + topic + "\n" +
            "Message: " + message;

        const subject = `New Contact Form Submission - ${topic}`;

        try {
            await sendEmail(emailMsg, subject);
            setIsSubmitted(true);
        } catch (err) {
            console.error(err);
        } finally {
            setIsSubmitting(false);
            setForm({
                name: "",
                email: "",
                phone: "",
                city: "",
                topic: "",
                message: "",
            });
        }
    };

    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const openWhatsApp = () => {
        const phone = "919473425998";
        const message = " Hello,Please share more details and any sample references regarding your services. Thank you.";
        const encoded = encodeURIComponent(message);

        const url = isMobile
            ? `https://wa.me/${phone}?text=${encoded}`
            : `https://web.whatsapp.com/send?phone=${phone}&text=${encoded}`;

        window.open(url, "_blank");
    };

    return (
        <div style={{
            padding: "130px 0 60px",
            backgroundColor: "var(--cream)"
        }}>
            <div className="container">
                {/* Background decorative elements */}
                <div className="position-absolute top-0 start-0 w-100 h-100" style={{ zIndex: -1 }}>
                    <div className="position-absolute top-0 end-0 w-50 h-50 d-none d-md-block"
                        style={{
                            background: "radial-gradient(circle, var(--gold) 0%, transparent 70%)",
                            opacity: 0.03,
                            transform: "translate(30%, -30%)"
                        }}
                    ></div>
                    <div className="position-absolute bottom-0 start-0 w-50 h-50 d-none d-md-block"
                        style={{
                            background: "radial-gradient(circle, var(--primary-blue) 0%, transparent 70%)",
                            opacity: 0.03,
                            transform: "translate(-30%, 30%)"
                        }}
                    ></div>
                </div>

                {/* Heading */}
                <motion.div
                    className="text-center mb-4 mb-md-5 position-relative px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-50px" }}
                    variants={containerVariants}
                >
                    <motion.h4
                        className="fw-bold display-6 display-md-5"
                        variants={fadeInUp}
                        style={{
                            fontFamily: "'Playfair Display', serif",
                            fontWeight: 600,
                            color: 'var(--primary-dark)',
                            background: "linear-gradient(135deg, var(--primary-dark) 0%, var(--gold) 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text"
                        }}
                    >
                        Let’s Get Connected 
                    </motion.h4>

                    <motion.p
                        className="fs-6 fs-md-5 mx-auto mb-3 mb-md-4"
                        variants={fadeInUp}
                        style={{
                            maxWidth: "750px",
                            color: "var(--text-primary)",
                            lineHeight: 1.7
                        }}
                    >
                        At <strong style={{ color: "var(--gold)" }}>RNA Law Chambers</strong>, we are committed to providing
                        strategic, reliable, and client-focused legal solutions. Whether you require legal consultation,
                        dispute resolution, corporate advisory, or representation before courts and tribunals, our team
                        is here to assist you with professionalism and integrity.
                    </motion.p>


                    <motion.div
                        className="d-flex justify-content-center gap-3 gap-md-4 flex-wrap mt-3 mt-md-4"
                        variants={fadeInUp}
                    >
                        {[
                            { icon: faUser, text: "Client-Centered Approach" },
                            { icon: faBuilding, text: "Corporate & Litigation Expertise" },
                            { icon: faCheckCircle, text: "Ethical & Trusted Counsel" }
                        ].map((item, index) => (
                            <motion.div
                                key={index}
                                className="d-flex align-items-center gap-2 p-2 p-md-3 rounded-3"
                                initial={{
                                    backgroundColor: "var(--white)",
                                    color: "var(--gold)",
                                    border: "1px solid var(--gold-light)"
                                }}
                                whileHover={{
                                    scale: 1.07,
                                    backgroundColor: "var(--gold)",
                                    color: "var(--white)",
                                    borderColor: "var(--gold)"
                                }}
                                transition={{ type: "spring", stiffness: 260, damping: 15 }}
                                style={{
                                    cursor: "pointer",
                                    fontSize: "0.9rem",
                                    transition: "var(--transition)"
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={item.icon}
                                    style={{ fontSize: "0.9rem" }}
                                />
                                <span className="fw-medium">{item.text}</span>
                            </motion.div>
                        ))}
                    </motion.div>
                </motion.div>

                {/* Content */}
                <motion.div
                    className="row g-4 g-md-5 align-items-start px-3 px-md-0"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    {/* Left — Contact Info */}
                    <motion.div className="col-lg-6 d-flex" variants={itemVariants}>
                        <div
                            className="card flex-fill h-100 position-relative overflow-hidden"
                            style={{
                                background: "linear-gradient(135deg, var(--white) 0%, var(--cream) 100%)",
                                boxShadow: "var(--shadow-medium)",
                                border: "1.5px solid var(--gold-light)",
                                borderRadius: "12px"
                            }}
                        >
                            {/* Decorative corner */}
                            <div className="position-absolute top-0 end-0 w-100 h-100 d-none d-md-block"
                                style={{
                                    background: "linear-gradient(45deg, transparent 70%, var(--gold) 70%)",
                                    opacity: 0.05
                                }}
                            ></div>

                            <div className="card-body p-3 p-md-4 p-lg-5 position-relative">
                                <h3
                                    className="fw-bold mb-3 mb-md-4 display-6"
                                    style={{
                                        fontFamily: "'Playfair Display', serif",
                                        color: "var(--primary-dark)",
                                        fontSize: "clamp(1.5rem, 4vw, 2.5rem)"
                                    }}
                                >
                                    Get in Touch
                                </h3>

                                <p
                                    className="mb-3 mb-md-4 fs-6 fs-md-5"
                                    style={{ color: "var(--text-primary)" }}
                                >
                                    Reach out to us for inquiries related to legal consultations, advisory services, dispute resolution, and court representation.
                                </p>

                                <div className="vstack gap-2 gap-md-3">
                                    {[
                                        {
                                            icon: faPhone,
                                            text: "Call:",
                                            detail: "+91 9473425998",
                                            link: "tel:+919473425998",
                                            description: "Available for quick queries during office hours only"
                                        },
                                        {
                                            icon: faWhatsapp,
                                            text: "WhatsApp:",
                                            detail: "+91 9473425998",
                                            onClick: () => openWhatsApp(),
                                            description: "Available for quick queries during office hours only"
                                        },
                                        {
                                            icon: faEnvelope,
                                            text: "Email:",
                                            detail: "rnalawchambers@gmail.com",
                                            link: isMobile
                                                ? "mailto:rnalawchambers@gmail.com"
                                                : "https://mail.google.com/mail/?view=cm&fs=1&to=rnalawchambers@gmail.com",
                                            description: "Available for General Enquiry",
                                        },
                                        {
                                            icon: faGlobe,
                                            text: "Website:",
                                            detail: "www.rnalawchambers.in",
                                            link: "https://www.rnalawchambers.in",
                                            description: "Learn More About Our Legal Services & Expertise"
                                        },
                                        {
                                            icon: faMapMarkerAlt,
                                            text: "Office:",
                                            detail: "Unit No. 8, 2nd Floor, Orchid Mall, Boring Road,Patna - 800013, Bihar, India",
                                            description: "Dedicated to Protecting Your Legal Rights"
                                        }
                                    ].map((item, index) => (
                                        <motion.div
                                            key={index}
                                            className="d-flex align-items-start p-3 p-md-4 rounded-3 rounded-md-4 position-relative overflow-hidden"
                                            style={{
                                                backgroundColor: "var(--white)",
                                                border: "1px solid var(--gold-light)",
                                                boxShadow: "var(--shadow-soft)",
                                                borderRadius: "8px"
                                            }}
                                            whileHover={{
                                                scale: 1.02,
                                                borderColor: "var(--gold)"
                                            }}
                                            transition={{ type: "spring", stiffness: 300 }}
                                        >
                                            {/* Hover effect background */}
                                            <div className="position-absolute top-0 left-0 w-100 h-100 d-none d-md-block"
                                                style={{
                                                    background: "linear-gradient(135deg, var(--gold) 0%, transparent 100%)",
                                                    opacity: 0,
                                                    transition: "opacity 0.3s ease"
                                                }}
                                            ></div>

                                            <FontAwesomeIcon
                                                icon={item.icon}
                                                className="fs-6 fs-md-5 me-2 me-md-3 position-relative z-1"
                                                style={{
                                                    color: "var(--gold)",
                                                    minWidth: "16px",
                                                    marginTop: "2px"
                                                }}
                                            />

                                            <div className="position-relative z-1 flex-grow-1">
                                                <div className="d-flex flex-column flex-md-row flex-wrap align-items-start align-items-md-center gap-1 gap-md-2 mb-1">
                                                    <strong style={{ color: "var(--primary-dark)", fontSize: "0.9rem" }}>{item.text}</strong>
                                                    {item.link ? (
                                                        <a
                                                            href={item.link}
                                                            className="fw-bold text-decoration-none"
                                                            style={{
                                                                color: "var(--gold)",
                                                                fontSize: "0.9rem",
                                                                wordBreak: "break-all"
                                                            }}
                                                            onMouseOver={(e) => e.target.style.color = "var(--primary-blue)"}
                                                            onMouseOut={(e) => e.target.style.color = "var(--gold)"}
                                                        >
                                                            {item.detail}
                                                        </a>
                                                    ) : (
                                                        <span
                                                            onClick={item.onClick}
                                                            className={item.onClick ? "clickable-span" : ""}
                                                            style={{
                                                                color: item.onClick ? "var(--gold)" : "var(--text-primary)",
                                                                fontSize: "0.9rem",
                                                                cursor: item.onClick ? "pointer" : "default",
                                                                fontWeight: item.onClick ? "bold" : "normal",
                                                                transition: "var(--transition)"
                                                            }}
                                                            onMouseOver={(e) => {
                                                                if (item.onClick) e.target.style.color = "var(--primary-blue)";
                                                            }}
                                                            onMouseOut={(e) => {
                                                                if (item.onClick) e.target.style.color = "var(--gold)";
                                                            }}
                                                        >
                                                            {item.detail}
                                                        </span>
                                                    )}
                                                </div>
                                                {item.description && (
                                                    <small className="text-muted" style={{ fontSize: "0.8rem", color: "var(--gray-medium)" }}>
                                                        {item.description}
                                                    </small>
                                                )}
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>

                                {/* Office hours */}
                                <motion.div
                                    className="mt-3 mt-md-4 p-3 p-md-4 rounded-3 rounded-md-4 d-flex align-items-center position-relative overflow-hidden"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: "1px solid var(--gold-light)",
                                        boxShadow: "var(--shadow-soft)",
                                        borderRadius: "8px"
                                    }}
                                    whileHover="animate"
                                    initial="initial"
                                    variants={pulseAnimation}
                                >
                                    <div className="position-absolute top-0 left-0 w-100 h-100 d-none d-md-block"
                                        style={{
                                            background: "linear-gradient(135deg, var(--gold) 0%, transparent 100%)",
                                            opacity: 0.05
                                        }}
                                    ></div>
                                    <FontAwesomeIcon
                                        icon={faClock}
                                        className="me-2 me-md-3 fs-6 fs-md-5 position-relative z-1"
                                        style={{ color: "var(--gold)" }}
                                    />
                                    <div className="position-relative z-1">
                                        <strong style={{ color: "var(--primary-dark)", fontSize: "0.9rem" }}>Office Hours:</strong>
                                        <div style={{ color: "var(--text-primary)", fontSize: "0.9rem", whiteSpace: "nowrap" }}>
                                            <div>Mon – Thur : 5:00 P.M. – 8:00 P.M.</div>
                                            <div>Sunday : 10:00 A.M. – 2:00 P.M.</div>
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Right — Contact Form */}
                    <motion.div className="col-lg-6 d-flex" variants={itemVariants}>
                        <div
                            className="card h-100 flex-fill position-relative overflow-hidden"
                            style={{
                                background: "linear-gradient(135deg, var(--white) 0%, var(--cream) 100%)",
                                boxShadow: "var(--shadow-medium)",
                                border: "1.5px solid var(--gold-light)",
                                borderRadius: "12px"
                            }}
                        >
                            {/* Decorative corner */}
                            <div className="position-absolute top-0 start-0 w-100 h-100 d-none d-md-block"
                                style={{
                                    background: "linear-gradient(-45deg, transparent 70%, var(--primary-blue) 70%)",
                                    opacity: 0.05
                                }}
                            ></div>

                            <div className="card-body p-3 p-md-4 p-lg-5 position-relative">
                                {isSubmitted ? (
                                    <motion.div
                                        className="text-center py-4 py-md-5"
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.5 }}
                                    >
                                        <motion.div
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                            transition={{ delay: 0.2, type: "spring" }}
                                        >
                                            <FontAwesomeIcon
                                                icon={faCheckCircle}
                                                className="mb-3"
                                                style={{ fontSize: "3rem", color: "var(--green-accent)" }}
                                            />
                                        </motion.div>
                                        <h4 className="fw-bold mb-3" style={{ color: "var(--primary-dark)", fontSize: "1.5rem" }}>
                                            Thank You!
                                        </h4>
                                        <p className="mb-4" style={{ fontSize: "0.9rem", color: "var(--gray-medium)" }}>
                                            Your message has been sent successfully. We'll get back to you within 24 hours.
                                        </p>
                                        <motion.button
                                            className="btn px-3 px-md-4 py-2"
                                            style={{
                                                backgroundColor: "var(--gold)",
                                                color: "var(--white)",
                                                fontSize: "0.9rem",
                                                border: "none",
                                                borderRadius: "6px"
                                            }}
                                            whileHover={{
                                                scale: 1.05,
                                                backgroundColor: "var(--gold-light)"
                                            }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={() => setIsSubmitted(false)}
                                        >
                                            Send Another Message
                                        </motion.button>
                                    </motion.div>
                                ) : (
                                    <>
                                        <h3
                                            className="fw-bold mb-3 mb-md-4 display-6"
                                            style={{
                                                fontFamily: "'Playfair Display', serif",
                                                color: "var(--primary-dark)",
                                                fontSize: "clamp(1.5rem, 4vw, 2.5rem)"
                                            }}
                                        >
                                            Contact Form
                                        </h3>

                                        <form onSubmit={handleSubmit}>
                                            <div className="row g-3 g-md-4">
                                                {/* Basic Fields */}
                                                {["name", "email", "phone", "city"].map((field, i) => {
                                                    const labelText =
                                                        field === "phone"
                                                            ? "Mobile Number"
                                                            : field === "city"
                                                                ? "City"
                                                                : field.charAt(0).toUpperCase() + field.slice(1);

                                                    return (
                                                        <div className="col-12 col-md-6" key={i}>
                                                            <motion.div
                                                                className="form-floating mb-3 mb-md-4"
                                                                whileHover={{ scale: 1.02 }}
                                                                transition={{ type: "spring", stiffness: 400, damping: 25 }}
                                                            >
                                                                <input
                                                                    type={field === "email" ? "email" : "text"}
                                                                    className="form-control"
                                                                    id={field}
                                                                    placeholder={labelText}
                                                                    value={form[field]}
                                                                    onChange={handleChange}
                                                                    required
                                                                    style={{
                                                                        backgroundColor: "var(--white)",
                                                                        minHeight: "55px",
                                                                        border: "1px solid var(--gold-light)",
                                                                        borderRadius: "8px",
                                                                        fontSize: "1rem",
                                                                        transition: "var(--transition)",
                                                                        boxShadow: "var(--shadow-soft)"
                                                                    }}
                                                                    onFocus={(e) => {
                                                                        e.target.style.borderColor = "var(--gold)";
                                                                        e.target.style.boxShadow = "0 0 6px 2px rgba(184, 134, 11, 0.25)";
                                                                    }}
                                                                    onBlur={(e) => {
                                                                        e.target.style.borderColor = "var(--gold-light)";
                                                                        e.target.style.boxShadow = "var(--shadow-soft)";
                                                                    }}
                                                                />
                                                                <label
                                                                    htmlFor={field}
                                                                    style={{
                                                                        color: "var(--gray-medium)",
                                                                        fontSize: "0.9rem",
                                                                        fontWeight: "500"
                                                                    }}
                                                                >
                                                                    {labelText}
                                                                </label>
                                                            </motion.div>
                                                        </div>
                                                    );
                                                })}

                                                {/* Select Topic */}
                                                <div className="col-12">
                                                    <motion.div
                                                        className="form-floating mb-3 mb-md-4 mt-2 mt-md-3"
                                                        whileHover={{ scale: 1.02 }}
                                                        transition={{ type: "spring", stiffness: 400, damping: 25 }}
                                                    >
                                                        <select
                                                            className="form-select"
                                                            id="topic"
                                                            value={form.topic}
                                                            onChange={handleChange}
                                                            required
                                                            style={{
                                                                backgroundColor: "var(--white)",
                                                                minHeight: "80px",
                                                                border: "1px solid var(--gold-light)",
                                                                borderRadius: "8px",
                                                                fontSize: "1rem",
                                                                transition: "var(--transition)",
                                                                cursor: "pointer",
                                                                boxShadow: "var(--shadow-soft)"
                                                            }}
                                                            onFocus={(e) => {
                                                                e.target.style.borderColor = "var(--gold)";
                                                                e.target.style.boxShadow = "0 0 6px 2px rgba(184, 134, 11, 0.25)";
                                                            }}
                                                            onBlur={(e) => {
                                                                e.target.style.borderColor = "var(--gold-light)";
                                                                e.target.style.boxShadow = "var(--shadow-soft)";
                                                            }}
                                                        >
                                                            <option value="">Select Your Legal Requirement</option>
                                                            <option>Legal Consultation</option>
                                                            <option>Civil / Criminal Litigation</option>
                                                            <option>Corporate & Commercial Advisory</option>
                                                            <option>Contract Drafting & Review</option>
                                                            <option>Regulatory & Compliance Matters</option>
                                                            <option>General Legal Query</option>
                                                        </select>

                                                        <label
                                                            htmlFor="topic"
                                                            style={{
                                                                color: "var(--gray-medium)",
                                                                fontSize: "0.9rem",
                                                                fontWeight: "500"
                                                            }}
                                                        >
                                                            How can we assist you?
                                                        </label>
                                                    </motion.div>
                                                </div>

                                                {/* Message */}
                                                <div className="col-12 mb-3 mt-5">
                                                    <motion.div
                                                        className="form-floating"
                                                        whileHover={{ scale: 1.01 }}
                                                        transition={{ type: "spring", stiffness: 400, damping: 25 }}
                                                    >
                                                        <textarea
                                                            className="form-control"
                                                            id="message"
                                                            value={form.message}
                                                            onChange={handleChange}
                                                            style={{
                                                                height: "180px",
                                                                backgroundColor: "var(--white)",
                                                                resize: "none",
                                                                border: "1px solid var(--gold-light)",
                                                                borderRadius: "8px",
                                                                fontSize: "1rem",
                                                                transition: "var(--transition)",
                                                                boxShadow: "var(--shadow-soft)"
                                                            }}
                                                            required
                                                            placeholder="Tell us about your requirements..."
                                                            onFocus={(e) => {
                                                                e.target.style.borderColor = "var(--gold)";
                                                                e.target.style.boxShadow = "0 0 6px 2px rgba(184, 134, 11, 0.25)";
                                                            }}
                                                            onBlur={(e) => {
                                                                e.target.style.borderColor = "var(--gold-light)";
                                                                e.target.style.boxShadow = "var(--shadow-soft)";
                                                            }}
                                                        ></textarea>
                                                        <label
                                                            htmlFor="message"
                                                            style={{
                                                                color: "var(--gray-medium)",
                                                                fontSize: "0.9rem",
                                                                fontWeight: "500"
                                                            }}
                                                        >
                                                            Your Message
                                                        </label>
                                                    </motion.div>
                                                </div>

                                                {/* Submit */}
                                                <div className="col-12 mt-5 mb-5">
                                                    <motion.button
                                                        className="btn w-100 py-3 fw-bold position-relative overflow-hidden border-0"
                                                        style={{
                                                            backgroundColor: isSubmitting ? "var(--primary-blue)" : "var(--gold)",
                                                            color: "var(--white)",
                                                            fontSize: "1rem",
                                                            borderRadius: "8px",
                                                            border: "2px solid transparent",
                                                            transition: "var(--transition)"
                                                        }}
                                                        type="submit"
                                                        disabled={isSubmitting}
                                                        whileHover={{
                                                            scale: isSubmitting ? 1 : 1.03,
                                                            boxShadow: isSubmitting ? "none" : "0 8px 25px rgba(184, 134, 11, 0.3)",
                                                            backgroundColor: isSubmitting ? "var(--primary-blue)" : "var(--gold-light)"
                                                        }}
                                                        whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                                                    >
                                                        {isSubmitting ? (
                                                            <motion.span
                                                                initial={{ opacity: 0 }}
                                                                animate={{ opacity: 1 }}
                                                            >
                                                                <motion.span
                                                                    animate={{ rotate: 360 }}
                                                                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                                                    className="me-2"
                                                                >
                                                                    ⏳
                                                                </motion.span>
                                                                Sending...
                                                            </motion.span>
                                                        ) : (
                                                            <>
                                                                <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                                                                Send Message
                                                            </>
                                                        )}

                                                        {/* Loading bar */}
                                                        {isSubmitting && (
                                                            <motion.div
                                                                className="position-absolute bottom-0 left-0 h-1"
                                                                style={{
                                                                    backgroundColor: "var(--white)",
                                                                    opacity: 0.5,
                                                                    borderRadius: "0 0 8px 8px"
                                                                }}
                                                                initial={{ width: "0%" }}
                                                                animate={{ width: "100%" }}
                                                                transition={{ duration: 1.5, ease: "linear" }}
                                                            />
                                                        )}
                                                    </motion.button>
                                                </div>
                                            </div>
                                        </form>
                                    </>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
            <div className="container mt-5">
                <div
                    className="ratio ratio-16x9"
                    style={{
                        border: "2px solid #000",
                        borderRadius: "8px",
                        overflow: "hidden"
                    }}
                >
                    <iframe
                        src="https://www.google.com/maps?q=RNA+Law+Chambers+Patna&output=embed"
                        title="Google Map - Rna Law Chambers"
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                    />
                </div>
            </div>



        </div>
    );
};